/**
 * Sessions Tab Component for TPRADashboard
 *
 * Displays comprehensive sessions list with server-side:
 * - Pagination (25 items per page default)
 * - Search (debounced 500ms, searches session title and vendor name)
 * - No sorting or filtering (sessions sorted by updated_at desc by default)
 *
 * Features:
 * - SHARED badge for collaborative sessions
 * - Status chips with color coding
 * - Relative timestamps ("2 hours ago")
 * - Actions dropdown with Continue/View + Download options
 * - Per-row loading states for async actions
 */

import { generateAndDownloadSessionReport } from "@/modules/agent/utils/downloadReport";
import { useGetTeamsListQuery } from "@/modules/auth/requests";
import { getAgentSession } from "@/modules/sessions/requests";
import type { MultiVendorAgentSession } from "@/modules/sessions/types";
import { handleContinueMultiVendorAssessment } from "@/modules/sessions/use-cases/agent-session.use-case";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { SearchField } from "@/shared/components/TableSearch";
import { ROUTES } from "@/shared/constants/routes";
import { usePagination } from "@/shared/hooks/usePagination";
import { useSearch } from "@/shared/hooks/useSearch";
import { addNotification } from "@/shared/states/notification";
import { cn } from "@nextui-org/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSessionsColumns } from "../constants/sessionsColumns";
import { useGetAllSessions } from "../requests/getSessions";
import type { SessionDetails } from "../types/sessions";

export function SessionsTab() {
  const navigate = useNavigate();
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});

  // Fetch ALL sessions at once (no params, client-side filtering)
  const { data: allSessions, isLoading, isFetching, isError, error } = useGetAllSessions();

  // Fetch team members for name resolution
  const { data: teamsData, dataUpdatedAt: teamsDataUpdatedAt } = useGetTeamsListQuery({ isEnabled: true });

  // Local state to hold sessions with resolved member names
  const [sessions, setSessions] = useState<SessionDetails[]>([]);

  // Update sessions when data or teams data changes (same pattern as Sessions module line 127-139)
  useEffect(() => {
    if (teamsDataUpdatedAt && allSessions) {
      // Resolve member names when teams data is available
      const updatedSessions = allSessions.map(session => ({
        ...session,
        created_by: teamsData?.memberIdNameMapping?.[session.created_by_id] || "",
      }));
      setSessions(updatedSessions);
    } else if (allSessions) {
      // If teams data not loaded yet, use sessions as-is
      setSessions(allSessions);
    }
  }, [allSessions, teamsData?.memberIdNameMapping, teamsDataUpdatedAt]);

  // Client-side hooks for pagination and search
  const { paginationState, setPaginationState, resetPageNumber } = usePagination();

  const { searchTerm, setSearchTerm, filteredData: searchFilteredSessions } = useSearch<SessionDetails>(
    sessions || [],
    {
      searchBy: (session: SessionDetails) => {
        const values: string[] = [];
        if (session?.name) values.push(session.name);
        if (session?.vendor_name) values.push(session.vendor_name);
        if (session?.created_by) values.push(session.created_by);
        return values;
      },
      resetPageNumber
    }
  );

  // Calculate pagination values from filtered data
  const totalSessions = searchFilteredSessions?.length ?? 0;
  const totalPages = Math.ceil(totalSessions / paginationState.rowsPerPage);

  // Client-side pagination (slice the filtered data)
  const paginatedSessions = useMemo(() => {
    const start = (paginationState.currentPage - 1) * paginationState.rowsPerPage;
    const end = paginationState.currentPage * paginationState.rowsPerPage;
    return searchFilteredSessions?.slice(start, end) ?? [];
  }, [searchFilteredSessions, paginationState.currentPage, paginationState.rowsPerPage]);

  /**
   * Continue/View session handler
   *
   * Loads the full session data with steps and navigates to agent workflow page.
   * Shows loading state during fetch and handles errors gracefully.
   */
  const handleContinueSession = useCallback(async (session: SessionDetails) => {
    // Prevent duplicate clicks
    if (isLoading || isFetching || loadingRows[session.id]) {
      return;
    }

    setLoadingRows((prev) => ({ ...prev, [session.id]: true }));

    try {
      // Fetch full session with steps
      const response = await getAgentSession(session.id);

      // Load into agent store using existing use-case
      // Cast to MultiVendorAgentSession since we filtered for MULTI_FILE_VENDOR_ASSESSMENT type
      await handleContinueMultiVendorAssessment({
        session: response.data.session as MultiVendorAgentSession,
        steps: response.data.steps,
      });

      // Navigate to agent workflow page
      navigate(`/${ROUTES.AGENT}/${session.id}`);
    } catch (error) {
      console.error("Error loading session:", error);
      addNotification({
        type: "error",
        title: "Error",
        message: "Failed to load assessment. Please try again.",
      });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [session.id]: false }));
    }
  }, [navigate, isLoading, isFetching, loadingRows]);

  /**
   * Handle row click to navigate to session
   * Same pattern as Sessions module (line 628-634)
   */
  const handleRowClick = useCallback(
    async ({ row }: { row: SessionDetails }) => {
      await handleContinueSession(row);
    },
    [handleContinueSession]
  );

  /**
   * Download report handler
   *
   * Fetches session steps and generates report in specified format.
   * Supports both summary_pdf and full report downloads.
   */
  const handleDownloadReport = useCallback(async (
    session: SessionDetails,
    format?: string
  ) => {
    setLoadingRows((prev) => ({ ...prev, [session.id]: true }));

    try {
      // Fetch session steps for download
      const response = await getAgentSession(session.id);

      await generateAndDownloadSessionReport(
        session.id,
        format,
        { steps: response.data.steps }
      );
    } catch (error) {
      console.error("Error downloading report:", error);
      addNotification({
        type: "error",
        title: "Download failed",
        message: "Failed to download report. Please try again.",
      });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [session.id]: false }));
    }
  }, []);

  // Get columns with action handlers
  const columns = getSessionsColumns(
    loadingRows,
    handleContinueSession,
    handleDownloadReport
  );

  return (
    <div className="h-full w-full flex flex-col gap-4 overflow-hidden mb-4">
      {/* Header: Title + Count | Search */}
      <div className="flex flex-row justify-between items-center gap-2 flex-shrink-0">
        {/* Left: Title + Count */}
        <div className="flex flex-row items-center gap-2 flex-grow">
          <h2 className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#11181C]">
            Sessions
          </h2>
          {!isLoading && !isFetching && (
            <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-[#F4F4F5] rounded-[45px]">
              <span className="font-['Inter'] font-medium text-[10px] leading-[16px] text-[#52525B]">
                {totalSessions}
              </span>
            </div>
          )}
        </div>

        {/* Right: Search */}
        <div className="flex flex-row items-center gap-2">
          <SearchField
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by Session Title or Third-Party Name"
            className="w-[420px]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="relative flex-1 min-h-0">
          {/* Loading overlay - only during refetch, not initial load */}
          {!isLoading && isFetching && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
              <LoadingSpinner />
            </div>
          )}
          <ZTableDeprecrated<SessionDetails>
          className={cn(
            'border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto flex-1'
          )}
          layout="auto"
          loading={isLoading || isFetching}
          rows={paginatedSessions}
          columns={columns}
          isHeaderSticky
          isCompact
          removeWrapper
          getRowId={(row) => row.id}
          onRowClick={handleRowClick}
          tableHeaderProps={{ className: 'shadow-none rounded-none' }}
          tableRowProps={(row, rowIndex) => ({
            className: cn(
              rowIndex === 0 ? 'border-none' : 'border-y',
              'group',
              'hover:bg-[#F2F2F2]',
              'cursor-pointer',
              row && row.id && loadingRows[row.id] && 'cursor-wait'
            ),
          })}
          classNames={{
            wrapper: '!shadow-none !rounded-none',
          }}
          aria-label="Sessions List"
          tableBodyProps={{
            emptyContent: isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner
                  containerClassName="bg-transparent relative"
                  className="mx-auto"
                />
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-red-500 text-sm font-medium">
                  Failed to load sessions
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {error?.message ?? 'An unexpected error occurred'}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-gray-500 text-sm">No sessions found</p>
              </div>
            ),
          }}
          />
        </div>
        <ZTablePagination
          totalPages={totalPages}
          paginationState={paginationState}
          setPagination={setPaginationState}
          isDisabled={isLoading || isFetching}
        />
      </div>
    </div>
  );
}
